import { Flex } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import audioPlayerHelp from '~/assets/sounds/mango-audio-player-help-menu.mp3'
import Text from '~/components/core/Text'
import KeyboardKey from '~/components/KeyboardKey'
import { AudioPlayer } from '~/components/shared/AudioPlayer'
import { AudioPlayerVariant } from '~/components/shared/componentStyles/AudioPlayer.style'
import { StyledLine } from '~/components/StyledLine'

const KeyboardShortcutsDetailWrapper = ({
  children,
}: React.PropsWithChildren) => {
  return (
    <Flex
      width={'100%'}
      height={'100%'}
      padding={'16px'}
      flexDir={'column'}
      backgroundColor="bg.subtle"
    >
      {children}
    </Flex>
  )
}

const TitleWrapper = ({ children }: React.PropsWithChildren) => {
  return (
    <Flex alignItems={'center'} justifyContent={'center'} marginBottom={'1rem'}>
      {children}
    </Flex>
  )
}

const DetailWrapper = ({ children }: React.PropsWithChildren) => {
  return (
    <Flex flex={1} marginTop={'1rem'}>
      {children}
    </Flex>
  )
}

const DetailColumn = ({ children }: React.PropsWithChildren) => {
  return (
    <Flex
      flex={1}
      flexDir={'column'}
      alignItems={'center'}
      marginLeft={'3rem'}
      marginRight={'3rem'}
    >
      {children}
    </Flex>
  )
}

const Row = ({ children }: React.PropsWithChildren) => {
  return (
    <Flex
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      marginBottom="1rem"
    >
      {children}
    </Flex>
  )
}

const KeyboardButtonWrapper = ({ children }: React.PropsWithChildren) => {
  return (
    <Flex gap="0.375rem">
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child) : null,
      )}
    </Flex>
  )
}

const AudioPlayerWrapper = ({ children }: React.PropsWithChildren) => {
  return <Flex marginBottom={'1rem'}>{children}</Flex>
}

const KeyboardShortcutsDetail = () => {
  const { t } = useTranslation()
  return (
    <KeyboardShortcutsDetailWrapper>
      <TitleWrapper>
        <Text
          text={t('shortcutKeysHelp.title')}
          color="var(--chakra-colors-fg-default)"
        />
      </TitleWrapper>
      <AudioPlayerWrapper>
        <AudioPlayer
          variant={AudioPlayerVariant.Inline}
          audioUri={audioPlayerHelp}
          showInfoButton={false}
          answerTimestamp={5251}
          showDownloadButton
        />
      </AudioPlayerWrapper>
      <StyledLine marginTop={10} marginBottom={10} />
      <DetailWrapper>
        <DetailColumn>
          <Row>
            <KeyboardButtonWrapper>
              <KeyboardKey text={t('shortcutKeysHelp.keyboardKeys.spaceBar')} />
            </KeyboardButtonWrapper>
            <Text
              color="var(--chakra-colors-fg-default)"
              text={t('shortcutKeysHelp.playPause')}
            />
          </Row>
          <Row>
            <KeyboardButtonWrapper>
              <KeyboardKey text={t('shortcutKeysHelp.keyboardKeys.m')} />
            </KeyboardButtonWrapper>
            <Text
              color="var(--chakra-colors-fg-default)"
              text={t('shortcutKeysHelp.muteUnmute')}
            />
          </Row>
          <Row>
            <KeyboardButtonWrapper>
              <KeyboardKey
                text={t('shortcutKeysHelp.keyboardKeys.leftArrow', '←')}
              />
            </KeyboardButtonWrapper>
            <Text
              color="var(--chakra-colors-fg-default)"
              text={t('shortcutKeysHelp.rewind10s')}
            />
          </Row>
          <Row>
            <KeyboardButtonWrapper>
              <KeyboardKey
                text={t('shortcutKeysHelp.keyboardKeys.rightArrow', '→')}
              />
            </KeyboardButtonWrapper>
            <Text
              color="var(--chakra-colors-fg-default)"
              text={t('shortcutKeysHelp.advance10s')}
            />
          </Row>
          <Row>
            <KeyboardButtonWrapper>
              <KeyboardKey text={t('shortcutKeysHelp.keyboardKeys.shift')} />
              <KeyboardKey
                text={t('shortcutKeysHelp.keyboardKeys.leftArrow', '←')}
              />
            </KeyboardButtonWrapper>
            <Text
              color="var(--chakra-colors-fg-default)"
              text={t('shortcutKeysHelp.rewind3s')}
            />
          </Row>
          <Row>
            <KeyboardButtonWrapper>
              <KeyboardKey text={t('shortcutKeysHelp.keyboardKeys.shift')} />
              <KeyboardKey
                text={t('shortcutKeysHelp.keyboardKeys.rightArrow', '→')}
              />
            </KeyboardButtonWrapper>
            <Text
              color="var(--chakra-colors-fg-default)"
              text={t('shortcutKeysHelp.advance3s')}
            />
          </Row>
          <Text
            color="var(--chakra-colors-fg-muted)"
            text={t(
              'shortcutKeysHelp.scopeNote',
              'Mute and skip shortcuts work while keyboard focus is in the audio player.',
            )}
          />
        </DetailColumn>
      </DetailWrapper>
    </KeyboardShortcutsDetailWrapper>
  )
}

export default KeyboardShortcutsDetail
